import type { DeviceFirmwareUpdate } from 'homey-api';
import { createContext, useContext, useEffect, useEffectEvent, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { HomeyApiKind } from '../../../../lib/HomeyApiKind';
import { ToastManager } from '../../../../ToastManager.jsx';
import { useRowContext } from '../SettingsTable';

import { devicesStore } from '../../../../store/devices/DevicesStore.ts';
import type { V3DeviceProps } from '../../../../store/devices/DevicesStore.ts';

import { useDevice } from '../../../../store/devices/useDevices.ts';

type DeviceUpdatesSettingsTableRowContextValue = {
  progress: number | null;
};

const DeviceUpdatesSettingsTableRowContext =
  createContext<DeviceUpdatesSettingsTableRowContextValue>({
    progress: null,
  });

export function useDeviceUpdatesSettingsTableRowContext() {
  return useContext(DeviceUpdatesSettingsTableRowContext);
}

interface DeviceUpdatesSettingsTableRowProps {
  children: ReactNode;
}

export function DeviceUpdatesSettingsTableRow(props: DeviceUpdatesSettingsTableRowProps) {
  const { row } = useRowContext<V3DeviceProps>();
  const { device, deviceInstance } = useDevice({
    deviceId: String(row.key),
    apiKinds: [HomeyApiKind.V3_LOCAL, HomeyApiKind.V3_CLOUD],
  });
  const [progress, setProgress] = useState<number | null>(device?.firmwareUpdate?.progress ?? null);

  useEffect(() => {
    setProgress(device?.firmwareUpdate?.progress ?? null);
  }, [device?.firmwareUpdate?.progress]);

  const listener = useEffectEvent((state: Partial<DeviceFirmwareUpdate>) => {
    if (device == null || state == null) {
      return;
    }

    const hasProgressUpdate = Object.prototype.hasOwnProperty.call(state, 'progress');

    if (hasProgressUpdate) {
      setProgress(state.progress ?? null);
    }

    if (hasProgressUpdate && Object.keys(state).length === 1) {
      return;
    }

    if (device.firmwareUpdate == null) {
      return;
    }

    devicesStore.updateOneOptimistic(device.id, {
      firmwareUpdate: {
        ...device.firmwareUpdate,
        ...state,
      },
    });
  });

  useEffect(() => {
    if (deviceInstance == null) {
      return;
    }

    deviceInstance.on('firmware-update-state', listener);
    const leasePromise = deviceInstance.acquireConnection().catch((error) => {
      ToastManager.handleError(error);
      return null;
    });

    return function cleanup() {
      deviceInstance.off('firmware-update-state', listener);
      leasePromise
        .then(async (lease) => {
          await lease?.release();
        })
        .catch(ToastManager.handleError);
    };
  }, [deviceInstance]);

  const contextValue = useMemo(
    () => ({
      progress,
    }),
    [progress],
  );

  return (
    <DeviceUpdatesSettingsTableRowContext.Provider value={contextValue}>
      {props.children}
    </DeviceUpdatesSettingsTableRowContext.Provider>
  );
}
